import CSS from 'csstype';
import React from 'react';

import {
  getCustomTag,
  getFlexDirection,
  getResponsiveStyle,
  getSpacingStyle,
  ILayoutProps,
  ISpacingProps,
  mediaSize,
  spacing,
  widths,
} from '~/src/dash/components/layout/common';
import { useBaseState } from '~/src/dash/views/base/contexts';

interface IPanelsProps extends ILayoutProps, ISpacingProps {
  // autoSize will take up 100% width and height and add a vertical scrollbar
  autoSize?: boolean;
  // maxWidth will take up 100% width and auto height
  maxWidth?: boolean;
  // maxHeight will take up 100% height
  maxHeight?: boolean;
  // take up 100% width and height
  max?: boolean;
  // min-width will set itself to the default main window min-width
  // necessary for ensuring a parent horizontal scroll if needed
  minWidthMain?: boolean;
  // use relative if inserting into a layout component
  relative?: boolean;
  // add a visible vertical scrollbar
  scroll?: boolean;
  flex?: boolean;
  vertical?: boolean;
  // set a mediaSize breakpoint to change flex direction
  flexDirectionBreakSize?: mediaSize;
}

export const Panels = ({
  autoSize,
  maxWidth,
  maxHeight,
  max,
  minWidthMain,
  relative,
  scroll,
  flex,
  vertical,
  flexDirectionBreakSize,
  noPadding,
  extraSmallPadding,
  smallPadding,
  basePadding,
  largePadding,
  extraLargePadding,
  hugePadding,
  noHorizontalPadding,
  extraSmallHorizontalPadding,
  smallHorizontalPadding,
  horizontalPadding,
  largeHorizontalPadding,
  extraLargeHorizontalPadding,
  hugeHorizontalPadding,
  noVerticalPadding,
  extraSmallVerticalPadding,
  smallVerticalPadding,
  verticalPadding,
  largeVerticalPadding,
  extraLargeVerticalPadding,
  hugeVerticalPadding,
  paddingTop,
  paddingRight,
  paddingBottom,
  paddingLeft,
  marginTop,
  marginRight,
  marginBottom,
  marginLeft,
  style,
  responsive,
  extraLargeStyle,
  largeStyle,
  mediumStyle,
  smallStyle,
  extraSmallStyle,
  children,
  innerRef,
  className = '',
  tag,
  cypressID,
}: IPanelsProps) => {
  const defaultFlexDirection = vertical ? 'column' : 'row';
  const mediaSize = responsive || flexDirectionBreakSize ? useBaseState().mediaSize : 'extraSmall';
  const addOptionalStyles = () => {
    const styles = {} as CSS.Properties;
    if (flex) {
      styles.flex = '1 1 0%';
    }
    if (maxWidth) {
      styles.width = '100%';
      styles.height = 'auto';
    }
    if (maxHeight) {
      styles.height = '100%';
    }
    if (max) {
      styles.width = '100%';
      styles.height = '100%';
    }
    if (minWidthMain) {
      styles.minWidth = widths.MAIN_WINDOW_MIN_WIDTH;
    }
    if (autoSize) {
      styles.width = '100%';
      styles.height = '100%';
      styles.overflowX = 'hidden';
      styles.overflowY = 'hidden';
    }
    if (scroll) {
      styles.overflowY = 'auto';
    }
    return styles;
  };

  return (
    <div
      ref={innerRef}
      className={className}
      data-cy={cypressID}
      style={{
        ...getCustomTag('PANELS', tag),
        display: 'flex',
        flexDirection: getFlexDirection(flexDirectionBreakSize, mediaSize, defaultFlexDirection),
        position: relative ? 'relative' : 'absolute',
        top: 0,
        left: 0,
        maxHeight: '100vh',
        ...addOptionalStyles(),
        ...getSpacingStyle({
          defaultPadding: spacing.none,
          noPadding,
          extraSmallPadding,
          smallPadding,
          basePadding,
          largePadding,
          extraLargePadding,
          hugePadding,
          noHorizontalPadding,
          extraSmallHorizontalPadding,
          smallHorizontalPadding,
          horizontalPadding,
          largeHorizontalPadding,
          extraLargeHorizontalPadding,
          hugeHorizontalPadding,
          noVerticalPadding,
          extraSmallVerticalPadding,
          smallVerticalPadding,
          verticalPadding,
          largeVerticalPadding,
          extraLargeVerticalPadding,
          hugeVerticalPadding,
          paddingTop,
          paddingRight,
          paddingBottom,
          paddingLeft,
          marginTop,
          marginRight,
          marginBottom,
          marginLeft,
        }),
        ...style,
        ...getResponsiveStyle({
          responsive,
          mediaSize,
          extraLargeStyle,
          largeStyle,
          mediumStyle,
          smallStyle,
          extraSmallStyle,
        }),
      }}
    >
      {children}
    </div>
  );
};
